import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import NZTerritoryMap from "@/components/NZTerritoryMap";
import { MapPin, ArrowLeft, ExternalLink, Instagram, Sparkles } from "lucide-react";

export default function PublicGrowerPage() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | not_found

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    api
      .get(`/public/growers/${encodeURIComponent(slug)}`)
      .then(({ data }) => {
        if (cancelled) return;
        setProfile(data);
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("not_found");
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="club-theme min-h-screen" data-testid="grower-page-loading">
        <PublicHeader />
        <div className="max-w-4xl mx-auto px-6 py-32 text-center font-serif italic text-[var(--bloom-ink-muted)]">
          Loading the grower…
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (status === "not_found" || !profile) {
    return (
      <div className="club-theme min-h-screen" data-testid="grower-page-not-found">
        <PublicHeader />
        <section className="max-w-3xl mx-auto px-6 py-32 text-center">
          <Sparkles className="mx-auto mb-4 text-[var(--bloom-ink-muted)]" strokeWidth={1.5} />
          <div className="bloom-divider mb-4">✦ Not yet published ✦</div>
          <h1 className="font-serif text-4xl md:text-5xl mb-4">This grower&apos;s page isn&apos;t live yet.</h1>
          <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">
            Their farm may still be settling in. Have a look at the full network instead.
          </p>
          <Link to="/growers" className="bloom-btn bloom-btn-primary" data-testid="back-to-growers">
            <ArrowLeft size={14} className="inline mr-2" /> Back to all growers
          </Link>
        </section>
        <PublicFooter />
      </div>
    );
  }

  const farmName = profile.farm_name || "Unnamed farm";
  const displayName = profile.display_name || "";
  const locationLine = [profile.town_city, profile.region].filter(Boolean).join(", ");
  const varieties = profile.varieties || [];
  const gallery = profile.gallery_urls || [];

  return (
    <div className="club-theme min-h-screen" data-testid="public-grower-page">
      <PublicHeader />

      {/* Breadcrumb / back */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10">
        <Link
          to="/growers"
          className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-brand)] transition-colors"
          data-testid="grower-page-back-link"
        >
          <ArrowLeft size={14} /> Back to all growers
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="bloom-divider mb-6 text-left">✦ Founding Grower ✦</div>
            <h1
              className="font-serif text-5xl md:text-7xl leading-[0.95]"
              data-testid="grower-farm-name"
            >
              <em>{farmName}</em>
            </h1>
            {displayName && (
              <div
                className="mt-4 font-serif italic text-xl text-[var(--bloom-ink-soft)]"
                data-testid="grower-display-name"
              >
                With {displayName}
              </div>
            )}
            {locationLine && (
              <div
                className="mt-4 flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)]"
                data-testid="grower-location"
              >
                <MapPin size={12} /> {locationLine}
              </div>
            )}
            {profile.tagline && (
              <p
                className="mt-8 font-serif italic text-2xl text-[var(--bloom-ink)] max-w-xl leading-snug"
                data-testid="grower-tagline"
              >
                &quot;{profile.tagline}&quot;
              </p>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden bg-[var(--bloom-surface)] border border-[var(--bloom-border)]">
              {profile.hero_photo_url ? (
                <img
                  src={profile.hero_photo_url}
                  alt={farmName}
                  className="w-full h-full object-cover"
                  data-testid="grower-hero-photo"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-serif italic text-[var(--bloom-ink-muted)] text-center p-8">
                  {farmName}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      {profile.story && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-2">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] sticky top-24">
                The story
              </div>
            </div>
            <div className="lg:col-span-8">
              <p
                className="font-serif text-lg md:text-xl leading-[1.7] text-[var(--bloom-ink)] whitespace-pre-line"
                data-testid="grower-story"
              >
                {profile.story}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Varieties */}
      {varieties.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16" data-testid="grower-varieties-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-2">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] sticky top-24">
                Varieties grown
              </div>
            </div>
            <div className="lg:col-span-10 flex flex-wrap gap-2">
              {varieties.map((v, i) => (
                <span
                  key={`${v}-${i}`}
                  className="px-4 py-1.5 bg-[var(--bloom-surface)] border border-[var(--bloom-border)] text-sm font-serif italic"
                  style={{ borderRadius: 999 }}
                  data-testid="grower-variety-pill"
                >
                  {v}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20" data-testid="grower-gallery-section">
          <div className="bloom-divider mb-8 text-left">✦ From the farm ✦</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.map((u, i) => (
              <div
                key={`${u}-${i}`}
                className="aspect-square overflow-hidden bg-[var(--bloom-surface)] border border-[var(--bloom-border)]"
                data-testid="grower-gallery-image"
              >
                <img src={u} alt="" className="w-full h-full object-cover transition duration-500 hover:scale-[1.03]" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Region map */}
      {profile.region && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20" data-testid="grower-region-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="bloom-divider mb-4 text-left">✦ Their corner of Aotearoa ✦</div>
              <h2 className="font-serif text-3xl md:text-4xl mb-4">{profile.region}</h2>
              <p className="font-serif italic text-[var(--bloom-ink-soft)] leading-relaxed">
                Every grower in the Bloom Club holds a single region. {displayName || "This grower"} stewards {profile.region}, shaping the flowers and the season to its rhythm.
              </p>
            </div>
            <div className="lg:col-span-7 bloom-card p-5">
              <NZTerritoryMap
                pins={[{ region: profile.region, count: 1 }]}
                activeRegion={profile.region}
                size="compact"
                showLegend={false}
              />
            </div>
          </div>
        </section>
      )}

      {/* Links + CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-32" data-testid="grower-cta-section">
        {(profile.instagram_handle || profile.website_url) && (
          <div className="flex flex-wrap gap-6 mb-12 pt-8 border-t border-[var(--bloom-border)]">
            {profile.instagram_handle && (
              <span
                className="inline-flex items-center gap-2 font-serif text-base"
                data-testid="grower-instagram"
              >
                <Instagram size={16} className="text-[var(--bloom-brand)]" /> {profile.instagram_handle}
              </span>
            )}
            {profile.website_url && (
              <a
                href={profile.website_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 font-serif text-base underline text-[var(--bloom-brand)] hover:opacity-80"
                data-testid="grower-website-link"
              >
                <ExternalLink size={16} /> {profile.website_url.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>
        )}

        <div className="bloom-card p-10 md:p-14 text-center">
          <div className="bloom-divider mb-4">✦ Bring their blooms home ✦</div>
          <h3 className="font-serif text-3xl md:text-4xl mb-4">
            Subscribe to the <em>Bloom Club</em>.
          </h3>
          <p className="font-serif italic text-[var(--bloom-ink-soft)] max-w-xl mx-auto mb-8">
            Subscribers in {profile.region || "this region"} may be matched with {displayName || farmName} for their seasonal deliveries.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/subscribe" className="bloom-btn bloom-btn-primary" data-testid="grower-cta-subscribe">
              Choose a plan
            </Link>
            <Link to="/growers" className="bloom-btn" data-testid="grower-cta-back">
              Meet more growers
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
